import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { apiGetSaved, apiSaveName, apiDeleteSaved } from "@/lib/namable";
import { useAuth } from "@/context/AuthContext";
import { useModals } from "@/context/ModalContext";
import { toast } from "sonner";

const SavedCtx = createContext(null);
export const useSaved = () => useContext(SavedCtx);

export function SavedProvider({ children }) {
  const { user, refreshUsage, track } = useAuth();
  const { openAuth, openPaywall } = useModals();
  const [saved, setSaved] = useState([]);

  const refresh = useCallback(async () => {
    if (!user) {
      setSaved([]);
      return;
    }
    try {
      const data = await apiGetSaved();
      setSaved(data.saved || []);
    } catch (e) {
      /* ignore */
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isSaved = useCallback((name) => saved.some((s) => s.name === name), [saved]);

  const doSave = useCallback(
    async (result) => {
      const existing = saved.find((s) => s.name === result.name);
      if (existing) {
        setSaved((prev) => prev.filter((s) => s.id !== existing.id));
        try {
          await apiDeleteSaved(existing.id);
        } catch (e) {
          refresh();
        }
        refreshUsage();
        return;
      }
      try {
        const rec = await apiSaveName({
          name: result.name,
          domain: result.domain,
          score: result.score ?? result.commercial_score ?? 0,
          search_id: result.search_id || null,
        });
        setSaved((prev) => [...prev, rec]);
        track?.("name_saved", { name: result.name, domain: result.domain });
        refreshUsage();
      } catch (e) {
        if (e?.response?.status === 402) openPaywall("saved_limit");
        else toast.error("Couldn't save that name. Try again.");
      }
    },
    [saved, refresh, refreshUsage, openPaywall, track]
  );

  const toggleSave = useCallback(
    (result) => {
      if (!user) {
        openAuth({
          mode: "register",
          title: "Create an account to save",
          onSuccess: () => setTimeout(() => doSave(result), 150),
        });
        return;
      }
      doSave(result);
    },
    [user, openAuth, doSave]
  );

  const removeSaved = useCallback(
    async (id) => {
      setSaved((prev) => prev.filter((s) => s.id !== id));
      try {
        await apiDeleteSaved(id);
      } catch (e) {
        refresh();
      }
      refreshUsage();
    },
    [refresh, refreshUsage]
  );

  return (
    <SavedCtx.Provider value={{ saved, refresh, isSaved, toggleSave, removeSaved }}>
      {children}
    </SavedCtx.Provider>
  );
}
